import React, { useRef, useEffect, useCallback, useState } from 'react';
import classNames from 'classnames';

import css from './RichTextEditor.module.css';
import YouTubeModal from './YouTubeModal';
import LinkModal from './LinkModal';

const MAX_CHARACTERS = 3000;

// Toolbar button component
const ToolbarButton = ({ label, onClick, active, title }) => (
  <button
    type="button"
    className={classNames(css.toolbarButton, { [css.active]: active })}
    onMouseDown={e => {
      // Prevent button click from stealing focus from contenteditable div
      e.preventDefault();
    }}
    onClick={onClick}
    title={title || label}
  >
    {label}
  </button>
);

/**
 * Rich Text Editor component with toolbar and contenteditable area
 */
const RichTextEditor = ({
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder,
  maxLength = MAX_CHARACTERS,
  className,
  id,
  onManageDisableScrolling,
}) => {
  const editorRef = useRef(null);
  const [characterCount, setCharacterCount] = useState(0);
  const [activeFormats, setActiveFormats] = useState({});
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkInitialText, setLinkInitialText] = useState('');
  const savedRangeRef = useRef(null);
  const isInternalChange = useRef(false);

  // Update character count
  const updateCharacterCount = useCallback(() => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || '';
      setCharacterCount(text.length);
    }
  }, []);

  // Check active formatting
  const checkActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
    });
  }, []);

  // Initialize editor with value
  useEffect(() => {
    if (editorRef.current && value && !isInternalChange.current) {
      editorRef.current.innerHTML = value;
      updateCharacterCount();
    }
    isInternalChange.current = false;
  }, [value, updateCharacterCount]);

  // Handle input changes
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      const html = editorRef.current.innerHTML;
      onChange?.(html);
      updateCharacterCount();
      checkActiveFormats();
    }
  }, [onChange, updateCharacterCount, checkActiveFormats]);

  // Execute command helper
  const execCommand = useCallback((command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput();
  }, [handleInput]);

  // Toolbar action handlers
  const handleUndo = () => execCommand('undo');
  const handleRedo = () => execCommand('redo');
  const handleBold = () => execCommand('bold');
  const handleItalic = () => execCommand('italic');
  const handleUnderline = () => execCommand('underline');
  const handleBulletList = () => execCommand('insertUnorderedList');
  const handleOrderedList = () => execCommand('insertOrderedList');

  const handleHeading = (level) => {
    execCommand('formatBlock', `h${level}`);
  };

  const handleLink = () => {
    const selection = window.getSelection();
    let selectedText = '';
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
        selectedText = selection.toString();
      } else {
        savedRangeRef.current = null;
      }
    } else {
      savedRangeRef.current = null;
    }
    setLinkInitialText(selectedText);
    setShowLinkModal(true);
  };

  const handleLinkConfirm = ({ url, text } = {}) => {
    if (!url || !editorRef.current) return;

    editorRef.current.focus();

    const selection = window.getSelection();
    let range = savedRangeRef.current;

    const isValidRange = range && editorRef.current.contains(range.commonAncestorContainer);

    if (!isValidRange) {
      if (selection && selection.rangeCount > 0 && editorRef.current.contains(selection.getRangeAt(0).commonAncestorContainer)) {
        range = selection.getRangeAt(0).cloneRange();
      } else {
        range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
      }
    }

    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';

    const displayText = text || (range && !range.collapsed ? range.toString() : '') || url;
    a.textContent = displayText;

    range.deleteContents();
    range.insertNode(a);

    // Position cursor right after the inserted link
    const newRange = document.createRange();
    newRange.setStartAfter(a);
    newRange.collapse(true);

    if (selection) {
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    savedRangeRef.current = null;
    handleInput();
  };

  const handleYoutube = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      } else {
        savedRangeRef.current = null;
      }
    } else {
      savedRangeRef.current = null;
    }
    setShowYoutubeModal(true);
  };

  const handleYoutubeConfirm = (url) => {
    const videoIdMatch = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    );
    
    if (videoIdMatch && videoIdMatch[1] && editorRef.current) {
      const videoId = videoIdMatch[1];
      
      editorRef.current.focus();
      
      const selection = window.getSelection();
      let range = savedRangeRef.current;
      
      const isValidRange = range && editorRef.current.contains(range.commonAncestorContainer);
      if (!isValidRange) {
        if (selection && selection.rangeCount > 0 && editorRef.current.contains(selection.getRangeAt(0).commonAncestorContainer)) {
          range = selection.getRangeAt(0).cloneRange();
        } else {
          range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
        }
      }
      
      const embedDiv = document.createElement('div');
      embedDiv.className = css.youtubeEmbed;
      
      const iframe = document.createElement('iframe');
      iframe.width = '560';
      iframe.height = '315';
      iframe.src = `https://www.youtube.com/embed/${videoId}`;
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', 'true');
      
      embedDiv.appendChild(iframe);
      
      range.deleteContents();
      range.insertNode(embedDiv);
      
      // Add a clean paragraph following the video embed
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      if (embedDiv.nextSibling) {
        embedDiv.parentNode.insertBefore(p, embedDiv.nextSibling);
      } else {
        embedDiv.parentNode.appendChild(p);
      }

      const newRange = document.createRange();
      newRange.setStart(p, 0);
      newRange.collapse(true);

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
      
      savedRangeRef.current = null;
      handleInput();
    }
  };

  const closeYoutubeModal = useCallback(() => {
    setShowYoutubeModal(false);
  }, []);

// Replace the handlePaste function in your RichTextEditor.js with this fixed version:

const handlePaste = useCallback((e) => {
  e.preventDefault();
  
  const text = e.clipboardData.getData('text/plain');
  if (!text) return;
  
  // Force a synchronous update of character count before processing
  const currentText = editorRef.current?.innerText || '';
  const currentLength = currentText.length;
  
  // Get selected text length (text that will be replaced)
  let selectedLength = 0;
  const selection = window.getSelection();
  
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    // Make sure the selection is within our editor
    if (editorRef.current?.contains(range.commonAncestorContainer)) {
      // Get the actual text content of the selection
      const selectedText = range.toString();
      selectedLength = selectedText.length;
    }
  }
  
  // Calculate how much space we have
  // If we have selected text, it will be replaced, so we subtract it
  const availableSpace = maxLength - (currentLength - selectedLength);
  
  // Don't allow paste if no space available
  if (availableSpace <= 0) {
    return;
  }
  
  // Truncate the pasted text to fit within the limit
  const textToInsert = text.slice(0, availableSpace);
  
  // Insert the text (this will replace any selected text)
  document.execCommand('insertText', false, textToInsert);
  
  // CRITICAL: Force trim if somehow we went over the limit
  setTimeout(() => {
    if (editorRef.current) {
      const finalText = editorRef.current.innerText || '';
      if (finalText.length > maxLength) {
        // Trim to exact limit
        const trimmedText = finalText.slice(0, maxLength);
        editorRef.current.innerText = trimmedText;
        
        // Move cursor to end
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      // Update the HTML content and trigger onChange
      handleInput();
    }
  }, 0);
}, [maxLength, handleInput]);

  const handleKeyDown = (e) => {
    // Handle paste to strip formatting if needed
    checkActiveFormats();

    // Allow navigation and editing keys
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }

    // Check limit directly (optional, but consistent) or use state
    // Using state is usually fine for keydown as typing is slower than paste
    if (characterCount >= maxLength) {
      e.preventDefault();
    }
  };

  const handleSelectionChange = () => {
    checkActiveFormats();
  };

  // Add selection change listener
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  const editorClasses = classNames(css.root, className);
  const counterClasses = classNames(css.characterCounter, {
    [css.characterCounterError]: characterCount >= maxLength,
  });

  return (
    <div className={editorClasses}>
      {/* Toolbar */}
      <YouTubeModal
        isOpen={showYoutubeModal}
        onClose={closeYoutubeModal}
        onConfirm={handleYoutubeConfirm}
        onManageDisableScrolling={onManageDisableScrolling}
      />
      
      <LinkModal
        isOpen={showLinkModal}
        initialText={linkInitialText}
        onClose={() => setShowLinkModal(false)}
        onConfirm={handleLinkConfirm}
        onManageDisableScrolling={onManageDisableScrolling}
      />
      
      <div className={css.toolbar}>
        <ToolbarButton label="Undo" onClick={handleUndo} title="Undo" />
        <ToolbarButton label="Redo" onClick={handleRedo} title="Redo" />
        
        <span className={css.separator} />
        
        <ToolbarButton label="H1" onClick={() => handleHeading(1)} title="Heading 1" />
        <ToolbarButton label="H2" onClick={() => handleHeading(2)} title="Heading 2" />
        <ToolbarButton label="H3" onClick={() => handleHeading(3)} title="Heading 3" />
        <ToolbarButton label="H4" onClick={() => handleHeading(4)} title="Heading 4" />
        
        <span className={css.separator} />
        
        <ToolbarButton 
          label="Bold" 
          onClick={handleBold} 
          active={activeFormats.bold}
          title="Bold" 
        />
        <ToolbarButton 
          label="Italic" 
          onClick={handleItalic} 
          active={activeFormats.italic}
          title="Italic" 
        />
        <ToolbarButton 
          label="Underline" 
          onClick={handleUnderline} 
          active={activeFormats.underline}
          title="Underline" 
        />
        
        <span className={css.separator} />
        
        <ToolbarButton 
          label="Bullet list"
          onClick={handleBulletList}
          active={activeFormats.insertUnorderedList}
          title="Bullet List" 
        />
        <ToolbarButton 
          label="Ordered list"
          onClick={handleOrderedList}
          active={activeFormats.insertOrderedList}
          title="Ordered List" 
        />
        
        <span className={css.separator} />
        
        <ToolbarButton label="Link" onClick={handleLink} title="Insert Link" />
        <ToolbarButton label="Youtube" onClick={handleYoutube} title="Embed YouTube Video" />
      </div>

      {/* Editor area */}
      <div
        ref={editorRef}
        id={id}
        className={css.editor}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onDrop={(e) => e.preventDefault()}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      {/* Character counter */}
      <div className={counterClasses}>
        Characters: {characterCount}/{maxLength}
      </div>
    </div>
  );
};

export default RichTextEditor;
